import { CSSProperties, useCallback, useRef } from "react";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import TableRow from "components/Table/Row";
import TableWrapper from "components/Table/Wrapper";
import useTableSort from "components/Table/useTableSort";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import useTitle from "hooks/useTitle";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import { FILTERS_ORDER_SETTINGS_KEY as FILTERS_ORDER_SETTINGS_KEY_IDP_GROUPS } from "views/Account/Settings/IdpGroupMappingV2/constants";
import PageInfoHeader from "ds/components/PageInfoHeader";

import IdpGroupUsersListItem from "./ListItem";
import {
  ANALYTICS,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  ROW_HEADER_ID,
  SORT_CONFIG,
  IDP_GROUP_USERS_COLUMN_CONFIG,
  IDP_GROUP_USERS_COLUMNS_STORAGE_KEY,
} from "./constants";
import { useFilteredEntities } from "./useFilteredEntities";
import IdpGroupUsersEmpty from "./Empty";
import { IdpGroupViewManagedUser } from "../types";

type IdpGroupUsersProps = {
  users: IdpGroupViewManagedUser[];
  groupName: string;
};

const IdpGroupUsers = ({ users, groupName }: IdpGroupUsersProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const filteredUsers = useFilteredEntities(users, sortDescriptor);

  const isPageEmpty = filteredUsers.length === 0;

  useTitle(`Organization Settings · IdP Groups · ${groupName} · Users`);

  useBreadcrumbs(
    [
      {
        title: "Organization Settings",
      },
      {
        title: "Idp Groups",
        link: getBreadcrumbsBackUrl(
          "/settings/idp-group-mapping",
          FILTERS_ORDER_SETTINGS_KEY_IDP_GROUPS
        ),
      },
      {
        title: groupName,
      },
    ],
    []
  );

  const renderRow = useCallback(
    (
      item: Partial<IdpGroupViewManagedUser> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <IdpGroupUsersListItem user={item as IdpGroupViewManagedUser} />
        </TableRow>
      );
    },
    []
  );

  return (
    <>
      <PageInfoHeader title="Users">
        <SearchInput placeholder={SEARCH_PLACEHOLDERS.USERS} filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY} />
      </PageInfoHeader>

      <TableContextProvider
        columnsConfig={IDP_GROUP_USERS_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={IDP_GROUP_USERS_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
          {!!filteredUsers.length && (
            <Table ariaLabel="Users" items={filteredUsers} ref={virtualizedListContainerRef}>
              {renderRow}
            </Table>
          )}

          {!users?.length && <IdpGroupUsersEmpty />}
          {isPageEmpty && !!users?.length && (
            <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
          )}
        </TableWrapper>
      </TableContextProvider>
    </>
  );
};

export default IdpGroupUsers;
